import React, { useContext } from "react";
import _ from "lodash";
import BreakpointContext from "../../contexts/breakpointContext";
import useDraftPicks from "../../hooks/queryResults/draft/useDraftPicks";
import useDraftSettings from "../../hooks/queryResults/draft/useDraftSettings";
import useNflTeams from "../../hooks/queryResults/nfl/useNflTeams";
import useAllPlayers from "../../hooks/queryResults/useAllPlayers";
import useIsLoading from "../../hooks/queryResults/useIsLoading";
import NflUtil from "../../util/nflUtil";
import Util from "../../util/util";
import PlayerImageByPos from "../player/PlayerImageByPos";
import { getPlayerName } from "../player/PlayerName";
import Loading from "../ui/Loading";
import styled from "styled-components";

const StyledTable = styled.table`
  margin: 1rem 0;

  & td,
  & th {
    white-space: nowrap;
    height: 28px;
    padding: 0.25rem;
  }

  & th.heading {
    font-size: larger;
    background-color: #0078f8;
    color: white;
  }
`;

export default function Roster({ team }) {
  const breakpoints = useContext(BreakpointContext);
  const { draftPicksData, draftPicksQuery } = useDraftPicks();
  const { draftSettingsData, draftSettingsQuery } = useDraftSettings();
  const { nflTeamsData, nflTeamsQuery } = useNflTeams();
  const { playersData, playersQuery } = useAllPlayers();

  const isLoading = useIsLoading(
    draftPicksData,
    draftPicksQuery,
    draftSettingsData,
    draftSettingsQuery,
    nflTeamsData,
    nflTeamsQuery,
    playersData,
    playersQuery
  );
  if (isLoading) return <Loading />;

  if (!team) return null;

  const startingPositions = [
    "QB",
    "RB",
    "RB",
    "WR",
    "WR",
    "WR",
    "TE",
    "K",
    "DEF",
  ];

  const startingPositionObjs = startingPositions.map((pos) => ({
    pos,
    roundPickNum: null,
    playerName: null,
    nflTeamAbbr: null,
    bye: null,
  }));

  const benchSize =
    draftSettingsData?.settings.numRounds - startingPositionObjs.length;
  const benchPositionObjs = _.range(0, benchSize).map(() => ({
    pos: "B",
    roundPickNum: null,
    playerName: null,
    nflTeamAbbr: null,
    bye: null,
  }));

  const teamPicks = draftPicksData.picks.filter(
    (pick) => pick.team === team.obj.id
  );

  teamPicks.forEach((pick) => {
    let isStarter = false;
    if (pick.player) {
      const player = playersData.players[pick.player];
      const name = (
        <>{getPlayerName(player, { injury: 1, short: !breakpoints.lg })}</>
      );
      const nflTeamAbbr = NflUtil.formatNflTeamAbbr(player.nflTeamAbbr);
      const { pickNum } = pick;
      const round = Util.getRoundFromPickNum(
        pick.pickNum,
        draftSettingsData.settings.numTeams
      );
      let byeWeek = null;
      if (player.nflTeamAbbr) {
        const nflTeam = nflTeamsData.nflTeams[player.nflTeamAbbr];
        if (nflTeam) byeWeek = nflTeam.byeWeek;
      }

      const formatObj = (obj) => {
        obj.round = round;
        obj.roundPickNum = round + "-" + pickNum;
        obj.playerImage = (
          <PlayerImageByPos player={player} style={{ maxWidth: "18px" }} />
        );
        obj.playerName = name;
        obj.nflTeamAbbr = nflTeamAbbr;
        obj.bye = byeWeek;
        obj.playerFullName = getPlayerName(player, {
          short: 1,
          nfl: 1,
          injury: 1,
          bye: byeWeek,
        });
      };

      for (const spObj of startingPositionObjs) {
        if (player.pos === spObj.pos && !spObj.playerName) {
          isStarter = true;
          formatObj(spObj);
          break;
        }
      }

      if (!isStarter) {
        for (const bObj of benchPositionObjs) {
          if (!bObj.playerName) {
            bObj.pos = player.pos;
            formatObj(bObj);
            break;
          }
        }
      }
    }
  });

  const headerCells = (
    <>
      <th>Pos</th>
      <th title={"Round-Pick"}>Pk</th>
      <th colSpan={2}>Player</th>
      <th>NFL</th>
      <th>Bye</th>
    </>
  );

  const getObjCells = (obj, key) => {
    return (
      <tr key={key}>
        <td>{obj.pos}</td>
        <td className={"left"}>{obj.roundPickNum}</td>
        <td>{obj.playerImage}</td>
        <td className={"left"}>{obj.playerName}</td>
        <td className={"left"}>{obj.nflTeamAbbr}</td>
        <td>{obj.bye}</td>
      </tr>
    );
  };

  return (
    <StyledTable style={{ width: "100%" }}>
      <thead>
        <tr>
          <th colSpan={6} className={"heading"}>
            Starters
          </th>
        </tr>
        <tr>{headerCells}</tr>
      </thead>
      <tbody>
        {startingPositionObjs.map((sp, i) => {
          return getObjCells(sp, i);
        })}
        <tr>
          <th colSpan={6} className={"heading"}>
            Bench
          </th>
        </tr>
        <tr>{headerCells}</tr>
        {benchPositionObjs.map((bp, i) => {
          return getObjCells(bp, i);
        })}
      </tbody>
    </StyledTable>
  );
}
